import {
    MutableRefObject,
    RefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-use';
import { twMerge } from 'tailwind-merge';
import cn from 'classnames';
import { useOutsideClick } from 'src/hooks';
import useAuth from 'src/hooks/useAuth';
import { authSelector, isLoggedSelector } from 'src/selectors/auth';
import { isIABMSelector } from 'src/selectors/app';
import {
    Button,
    Checkbox,
    Element,
    Icon,
    Image,
    RouterLink,
    Title,
} from 'src/components';
import ZaloLoginButton from 'src/modules/shared/components/buttons/ZaloLoginButton';
import DropdownSetting from './dropdown';

function UserProfile() {
    const { auth } = useAuth();
    const location = useLocation();
    const isLogged = useSelector(isLoggedSelector);
    const isIABM = useSelector(isIABMSelector);

    const authState = useSelector(authSelector);
    const [isExpand, setIsExpand] = useState<boolean>(false);
    const btnRef: MutableRefObject<any> = useRef<RefObject<any>>(null);
    const [checked, setChecked] = useState<boolean>(false);

    useEffect(() => {
        if (!isLogged) {
            auth();
        }
    }, [isLogged]);

    useEffect(() => {
        setIsExpand(false);
    }, [location]);

    const handleClick = useCallback(
        (evt: React.MouseEvent) => {
            setIsExpand(!isExpand);
        },
        [isExpand]
    );

    useEffect(() => {
        (window as any).authState = authState;
    }, [authState]);

    useOutsideClick([btnRef], (isExpand) => {
        if (!isExpand) {
            setIsExpand(false);
        }
    });

    const renderTriggerButton = useCallback(() => {
        if (!isLogged) {
            return (
                <Icon className="text-[1.8rem] text-[#6c757d]" icon="user" />
            );
        }
        return (
            <>
                <Image
                    className={cn(
                        'w-[23px] h-[23px]',
                        'rounded-full',
                        'align-top overflow-hidden'
                    )}
                    src={authState.avatar}
                ></Image>
            </>
        );
    }, [isLogged, authState]);

    const onChange = useCallback(() => {
        setChecked(!checked);
    }, [checked]);

    const renderDropdownContent = useCallback(() => {
        if (!isLogged) {
            return (
                <Element className={cn('login-dropdown', 'p-[15px]')}>
                    <Title>Tài khoản</Title>
                    <Element
                        className={cn(
                            'leading-[1.3]',
                            'text-[#777]',
                            'mb-[10px]'
                        )}
                    >
                        Bạn đồng ý với việc Đăng nhập/Đăng ký từ tài khoản liên
                        kết Zalo
                    </Element>
                    <Checkbox
                        className="flex items-start mb-[10px] [&_a]:underline"
                        classNameLabel="text-[1.4rem] font-bold"
                        classNameCheckbox="p-[5px] rounded-none checked:after:border-inherit"
                        checked={checked}
                        onChange={onChange}
                    >
                        Tôi đã đọc và đồng ý với{' '}
                        <RouterLink to="/staticpages/termsofuse.epi" newTab>
                            Điều khoản sử dụng
                        </RouterLink>{' '}
                        và{' '}
                        <RouterLink to="/staticpages/privacy.epi" newTab>
                            Chính sách bảo mật
                        </RouterLink>
                    </Checkbox>
                    <Element className={checked ? '' : 'disabled'}>
                        <ZaloLoginButton />
                    </Element>
                </Element>
            );
        }
        return <DropdownSetting />;
    }, [isLogged, checked]);

    if (isIABM) {
        return null;
    }

    return (
        <Element className={'user-profile relative'} domRef={btnRef}>
            <Button
                className="flex items-center p-[10px]"
                onClick={handleClick}
                title="Cá nhân"
            >
                {renderTriggerButton()}
            </Button>
            <Element
                className={twMerge(
                    'user-setting-dropdown',
                    'absolute right-[-18px] top-[40px]',
                    'px-0 py-[5px]',
                    'text-[1.4rem] leading-[22px] font-normal',
                    'border border-solid border-[#e4e4e4]',
                    'w-[200px] rounded-[5px]',
                    'bg-white',
                    'shadow-[0_0_3px_0_rgba(0,0,0,0.2)]',
                    'invisible',
                    'after:clear-both',
                    'after:w-0 after:h-0',
                    'after:absolute after:bottom-full after:right-[30px]',
                    'after:border-b-[#fff] after:border-solid after:border-[5px]',
                    'before:clear-both',
                    'before:w-0 before:h-0',
                    'before:absolute before:bottom-full before:right-[29px]',
                    'before:border-b-[#e4e4e4] before:border-solid before:border-[6px]',
                    cn({
                        ['visible opacity-100']: isExpand,
                    })
                )}
            >
                {renderDropdownContent()}
            </Element>
        </Element>
    );
}

export default UserProfile;
